/**
 * Load-into-a-container plumbing: loading placeholder, error state with retry,
 * abort on teardown, and no rendering of results from a superseded request.
 */

import { replaceChildren } from "./dom";
import { describeError, isAbort } from "./errors";
import { errorState, loadingState } from "../components/states";

export interface AsyncViewOptions<T> {
  readonly container: HTMLElement;
  readonly load: (signal: AbortSignal) => Promise<T>;
  readonly render: (value: T) => Node;
  readonly loadingLabel?: string;
}

export interface AsyncView {
  readonly reload: () => void;
  readonly dispose: () => void;
}

export function runAsyncView<T>(options: AsyncViewOptions<T>): AsyncView {
  let controller: AbortController | null = null;
  let disposed = false;

  const run = (): void => {
    if (disposed) {
      return;
    }
    controller?.abort();
    const active = new AbortController();
    controller = active;

    replaceChildren(options.container, loadingState(options.loadingLabel));

    void (async (): Promise<void> => {
      try {
        const value = await options.load(active.signal);
        if (active.signal.aborted || disposed) {
          return;
        }
        replaceChildren(options.container, options.render(value));
      } catch (error: unknown) {
        if (isAbort(error) || active.signal.aborted || disposed) {
          return;
        }
        replaceChildren(options.container, errorState({ message: describeError(error), onRetry: run }));
      }
    })();
  };

  run();

  return {
    reload: run,
    dispose(): void {
      disposed = true;
      controller?.abort();
      controller = null;
    },
  };
}
